import { useTranslation, useTranslate } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PopoverSelect } from 'module/ui/components/molecules/popover-select';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faUpload, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { DocumentationAgent } from 'api/document-agent/types';
import { useSearchCompanyEmailsQuery } from 'queries/emails';
import { useCompanyWiseSourcesQuery } from 'queries/meeting';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import styled from 'styled-components';
import type { Option } from 'module/ui/components/atoms/select';
import { getIconForFileType } from 'components/organisms/knowledge-base/file-viewer/icon-for-file-type';

const FormSection = styled.div`
  border: 1px solid var(--gray-10);
  border-radius: var(--border-radius-large);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 768px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: var(--line-height-01);
  color: var(--text-primary);
`;

const SectionHeading = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: var(--line-height-01);
  color: var(--text-primary);
`;

const SelectWrapper = styled.div`
  margin-bottom: 16px;
`;

const TriggerContent = styled.div`
  color: var(--text-secondary);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const CreateButton = styled(Button).attrs({
  variant: 'primary',
})`
  white-space: nowrap;
`;

const FileUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  margin-bottom: var(--spacing-06);
`;

const UploadButton = styled(Button)`
  width: 100%;
  height: 64px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-03);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
`;

const FileButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
  margin-top: var(--spacing-03);
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-02);
  border: 1px solid var(--divider-subtle);
  border-radius: 8px;
  padding: var(--spacing-03);
  background: var(--coolest-gray-10);
  height: 32px;
`;

const FileContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-05);
  flex: 1;
  .rl-icon {
    flex-shrink: 0;
  }
`;

const FileName = styled.span`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--line-height-02);
  letter-spacing: var(--letter-spacing-01);
  color: var(--text-primary);
`;

const HiddenInput = styled.input`
  display: none;
`;

export type UploadFile = {
  lastModified: number;
  lastModifiedDate: Date;
  name: string;
  originFileObj: File;
  percent: number;
  size: number;
  type: string;
  uid: string;
};

export interface AgentFormData {
  selectedMeetings: number[];
  selectedEmails: number[];
  agentInputValues: Record<string, unknown>;
  files: Record<string, UploadFile[]>;
}

interface AgentDataCollectionFormProps {
  agentData: DocumentationAgent;
  companyId: number;
  onSubmit: (data: AgentFormData) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

// File upload field component
const FileUploadField = ({
  field,
  files,
  onFileChange,
  onRemoveFile,
}: {
  field: any;
  files: UploadFile[];
  onFileChange: (fieldName: string, info: any) => void;
  onRemoveFile: (fieldName: string, fileUid: string) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFilesLabel = useTranslation('agent_data_collection.upload_files');
  const uploadFileLabel = useTranslation('agent_data_collection.upload_file');

  return (
    <FileUploadSection>
      <Label>
        {field.name || uploadFilesLabel}
        {field.required && <span style={{ color: 'red' }}> *</span>}
      </Label>
      {field.description && (
        <div
          style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginBottom: '8px',
          }}
        >
          {field.description}
        </div>
      )}
      <UploadButton
        variant="secondary"
        onClick={() => {
          fileInputRef.current?.click();
        }}
      >
        <FontAwesomeIcon icon={faUpload} size={12} />
        {uploadFileLabel}
      </UploadButton>
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept=".pdf,.md,.txt,.docx,.doc"
        multiple
        onChange={(e) => {
          const selectedFiles = Array.from(e.target.files || []);
          const fileList = selectedFiles.map((file, index) => ({
            uid: `${field.name}-${Date.now()}-${index}`,
            name: file.name,
            status: 'done' as const,
            originFileObj: file,
            lastModified: file.lastModified,
            lastModifiedDate: new Date(file.lastModified),
            percent: 100,
            size: file.size,
            type: file.type,
          }));

          onFileChange(field.name, { fileList });
          // Reset the input value so the same file can be selected again if needed
          if (e.target) {
            (e.target as HTMLInputElement).value = '';
          }
        }}
      />
      {files.length > 0 && (
        <FileButtonsContainer>
          {files.map((file) => (
            <FileItem key={file.uid}>
              <FileContent>
                <FontAwesomeIcon
                  icon={getIconForFileType(file.name)}
                  size={12}
                />
                <FileName>{file.name}</FileName>
              </FileContent>
              <Button
                variant="ghost"
                size="small"
                leftIcon={<FontAwesomeIcon icon={faXmark} size={12} />}
                onClick={() => {
                  onRemoveFile(field.name, file.uid);
                }}
              />
            </FileItem>
          ))}
        </FileButtonsContainer>
      )}
    </FileUploadSection>
  );
};

export const AgentDataCollectionForm = ({
  agentData,
  companyId,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: AgentDataCollectionFormProps) => {
  const [selectedMeetingOptions, setSelectedMeetingOptions] = useState<
    Option[]
  >([]);
  const [selectedEmailOptions, setSelectedEmailOptions] = useState<Option[]>(
    [],
  );
  const [_meetingSearchTerm, setMeetingSearchTerm] = useState('');
  const [_emailSearchTerm, setEmailSearchTerm] = useState('');
  const [meetingOffset, setMeetingOffset] = useState(0);
  const [emailOffset, setEmailOffset] = useState(0);
  const [currentStep, setCurrentStep] = useState<
    'meeting-selection' | 'agent-inputs'
  >('meeting-selection');

  // Accumulated options for pagination
  const [accumulatedMeetingOptions, setAccumulatedMeetingOptions] = useState<
    Option[]
  >([]);
  const [accumulatedEmailOptions, setAccumulatedEmailOptions] = useState<
    Option[]
  >([]);

  const [hasMoreMeetings, setHasMoreMeetings] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(false);
  const [agentInputValues, setAgentInputValues] = useState<
    Record<string, unknown>
  >({});
  const [filesByField, setFilesByField] = useState<
    Record<string, UploadFile[]>
  >({});

  const meetingLimit = 25;
  const emailLimit = 25;

  const hasAgentInputs =
    agentData && agentData.input_fields && agentData.input_fields.length > 0;

  // Filter only file type input fields
  const fileInputFields = useMemo(() => {
    if (!agentData?.input_fields) {
      return [];
    }

    return agentData.input_fields.filter(
      (input: any) =>
        input.field_type === 'file_upload' || input.field_type === 'UPLOAD',
    );
  }, [agentData?.input_fields]);

  const hasFileInputFields = fileInputFields.length > 0;

  const hasMeetingSource = agentData?.configuration?.sources?.some(
    (source) => source.pageType?.toLowerCase() === 'meeting',
  );
  const hasEmailSource = agentData?.configuration?.sources?.some(
    (source) => source.pageType?.toLowerCase() === 'email',
  );

  // Extract templateIds from agentData configuration sources (only for meeting sources)
  const meetingTemplateIds = useMemo(() => {
    if (!agentData?.configuration?.sources) {
      return [];
    }
    const allTemplateIds: number[] = [];

    agentData.configuration.sources
      .filter((source) => source.pageType?.toLowerCase() === 'meeting')
      .forEach((source) => {
        if (source.templateIdList && Array.isArray(source.templateIdList)) {
          allTemplateIds.push(...source.templateIdList);
        }
      });

    // Remove duplicates
    return Array.from(new Set(allTemplateIds));
  }, [agentData?.configuration?.sources]);

  const { data: meetingsData, isLoading: isMeetingsLoading } =
    useCompanyWiseSourcesQuery(
      {
        sourceType: 'MEETING',
        companyId,
        limit: meetingLimit,
        offset: meetingOffset,
        templateIds:
          meetingTemplateIds.length > 0 ? meetingTemplateIds : undefined,
        isEnabled: true,
      },
      {
        enabled:
          agentData.configuration.acceptSourcesAtRuntime &&
          companyId > 0 &&
          !!hasMeetingSource,
        refetchOnMount: 'always',
      },
    );

  const { data: emailsData, isLoading: isEmailsLoading } =
    useSearchCompanyEmailsQuery(
      companyId,
      {
        offset: emailOffset,
        limit: emailLimit,
      },
      {
        enabled:
          agentData.configuration.acceptSourcesAtRuntime &&
          companyId > 0 &&
          !!hasEmailSource,
        refetchOnMount: 'always' as const,
      },
    );

  const noSubjectLabel = useTranslation('agent_data_collection.no_subject');
  const cancelLabel = useTranslation('agent_data_collection.cancel');
  const nextLabel = useTranslation('agent_data_collection.next');
  const generateLabel = useTranslation('agent_data_collection.generate');

  // Update hasMore flags
  useEffect(() => {
    if (meetingsData) {
      setHasMoreMeetings(meetingsData.hasMore);
    }
  }, [meetingsData]);

  useEffect(() => {
    if (emailsData) {
      const totalFetched = emailOffset + emailLimit;

      setHasMoreEmails(totalFetched < emailsData.count);
    }
  }, [emailsData, emailOffset, emailLimit]);

  // Accumulate meeting options when new data arrives
  useEffect(() => {
    if (meetingsData?.data) {
      const newOptions = meetingsData.data.map((meeting) => ({
        label: meeting.meetingName,
        value: meeting.meetingId,
      }));

      if (meetingOffset === 0) {
        // Reset on first load or search reset
        setAccumulatedMeetingOptions(newOptions);
      } else {
        // Append new options for pagination
        setAccumulatedMeetingOptions((prev) => {
          const existingValues = prev.map((opt) => opt.value);
          const uniqueNewOptions = newOptions.filter(
            (opt) => !existingValues.includes(opt.value),
          );

          return [...prev, ...uniqueNewOptions];
        });
      }
    }
  }, [meetingsData, meetingOffset]);

  // Accumulate email options when new data arrives
  useEffect(() => {
    if (emailsData?.emailThreads) {
      const newOptions = emailsData.emailThreads.map((email) => ({
        label: email.subject || noSubjectLabel,
        value: email.emailThreadId,
      }));

      if (emailOffset === 0) {
        // Reset on first load or search reset
        setAccumulatedEmailOptions(newOptions);
      } else {
        // Append new options for pagination
        setAccumulatedEmailOptions((prev) => {
          const existingValues = prev.map((opt) => opt.value);
          const uniqueNewOptions = newOptions.filter(
            (opt) => !existingValues.includes(opt.value),
          );

          return [...prev, ...uniqueNewOptions];
        });
      }
    }
  }, [emailsData, emailOffset, noSubjectLabel]);

  // Reset all state when agent changes (e.g., when canceling and clicking agent again)
  useEffect(() => {
    setSelectedMeetingOptions([]);
    setSelectedEmailOptions([]);
    setMeetingSearchTerm('');
    setEmailSearchTerm('');
    setMeetingOffset(0);
    setEmailOffset(0);
    setCurrentStep('meeting-selection');
    setAgentInputValues({});
    setFilesByField({});
  }, [agentData.id]);

  const meetingOptions = accumulatedMeetingOptions;
  const emailOptions = accumulatedEmailOptions;

  const selectedMeetingValues = selectedMeetingOptions;
  const selectedEmailValues = selectedEmailOptions;

  const handleMeetingsChange = (value: Option[]) => {
    setSelectedMeetingOptions(value);
  };

  const handleEmailsChange = (value: Option[]) => {
    setSelectedEmailOptions(value);
  };

  // Handlers for loading more data
  const handleLoadMoreMeetings = useCallback(() => {
    setMeetingOffset((prev) => prev + meetingLimit);
  }, [meetingLimit]);

  const handleLoadMoreEmails = useCallback(() => {
    setEmailOffset((prev) => prev + emailLimit);
  }, [emailLimit]);

  const onFileChange = useCallback((fieldName: string, info: any) => {
    setFilesByField((prev) => ({
      ...prev,
      [fieldName]: info.fileList,
    }));
  }, []);

  const handleCancel = () => {
    setSelectedMeetingOptions([]);
    setSelectedEmailOptions([]);
    setMeetingSearchTerm('');
    setEmailSearchTerm('');
    setMeetingOffset(0);
    setEmailOffset(0);
    setCurrentStep('meeting-selection');
    setAgentInputValues({});
    setFilesByField({});
    onCancel();
  };

  const handleNextStep = () => {
    setCurrentStep('agent-inputs');
  };

  const handleCreate = async () => {
    const selectedMeetingIds = selectedMeetingOptions.map(
      (o) => o.value as number,
    );
    const selectedEmailIds = selectedEmailOptions.map((o) => o.value as number);

    await onSubmit({
      selectedMeetings: selectedMeetingIds,
      selectedEmails: selectedEmailIds,
      agentInputValues,
      files: filesByField,
    });
  };

  const showAgentInputs =
    ((hasAgentInputs || hasFileInputFields) &&
      currentStep === 'agent-inputs') ||
    (hasFileInputFields && !agentData.configuration.acceptSourcesAtRuntime);

  const translate = useTranslate();
  const sourcesHeading = useTranslation('select_sources');
  const meetingsHeading = useTranslation('meetings');
  const emailsHeading = useTranslation('email');
  const selectSourceMeetingsLabel = useTranslation(
    'agent_data_collection.select_source_meetings',
  );
  const noMeetingsSelectedLabel = useTranslation(
    'agent_data_collection.no_meetings_selected',
  );
  const noEmailsSelectedLabel = useTranslation(
    'agent_data_collection.no_emails_selected',
  );
  const selectSourceEmailsLabel = useTranslation(
    'agent_data_collection.select_source_emails',
  );

  return (
    <FormSection>
      {(agentData.configuration.acceptSourcesAtRuntime ||
        hasFileInputFields) && (
        <SectionHeading>{sourcesHeading}</SectionHeading>
      )}
      {agentData.configuration.acceptSourcesAtRuntime &&
        currentStep === 'meeting-selection' && (
          <>
            {hasMeetingSource && (
              <div>
                <Label>{meetingsHeading}</Label>
                <SelectWrapper>
                  <PopoverSelect
                    title={selectSourceMeetingsLabel}
                    triggerContent={
                      <TriggerContent>
                        {selectedMeetingOptions.length === 0
                          ? noMeetingsSelectedLabel
                          : translate(
                              'agent_data_collection.meeting_selected',
                              {
                                count: selectedMeetingOptions.length,
                              },
                            )}
                      </TriggerContent>
                    }
                    isMulti
                    options={meetingOptions}
                    value={selectedMeetingValues}
                    onChange={handleMeetingsChange}
                    isLoading={isMeetingsLoading}
                    onLoadMore={handleLoadMoreMeetings}
                    hasMore={hasMoreMeetings}
                    onInputChange={(value, action) => {
                      if (action.action === 'input-change') {
                        setMeetingSearchTerm(value);
                      }
                    }}
                    checkboxColor="black"
                  />
                </SelectWrapper>
              </div>
            )}

            {hasEmailSource && (
              <div>
                <Label>{emailsHeading}</Label>
                <SelectWrapper>
                  <PopoverSelect
                    title={selectSourceEmailsLabel}
                    triggerContent={
                      <TriggerContent>
                        {selectedEmailOptions.length === 0
                          ? noEmailsSelectedLabel
                          : translate('agent_data_collection.email_selected', {
                              count: selectedEmailOptions.length,
                            })}
                      </TriggerContent>
                    }
                    isMulti
                    options={emailOptions}
                    value={selectedEmailValues}
                    onChange={handleEmailsChange}
                    isLoading={isEmailsLoading}
                    onLoadMore={handleLoadMoreEmails}
                    hasMore={hasMoreEmails}
                    onInputChange={(value, action) => {
                      if (action.action === 'input-change') {
                        setEmailSearchTerm(value);
                      }
                    }}
                    checkboxColor="black"
                  />
                </SelectWrapper>
              </div>
            )}

            {(hasAgentInputs || hasFileInputFields) && (
              <ActionButtons>
                <Button variant="secondary" onClick={handleCancel}>
                  {cancelLabel}
                </Button>
                <Button variant="primary" onClick={handleNextStep}>
                  {nextLabel}
                </Button>
              </ActionButtons>
            )}

            {!hasAgentInputs && !hasFileInputFields && (
              <ActionButtons>
                <Button variant="secondary" onClick={handleCancel}>
                  {cancelLabel}
                </Button>
                <CreateButton
                  onClick={handleCreate}
                  disabled={
                    isSubmitting ||
                    !(
                      (hasMeetingSource && selectedMeetingOptions.length > 0) ||
                      (hasEmailSource && selectedEmailOptions.length > 0)
                    )
                  }
                  leftIcon={<FontAwesomeIcon icon={faPlus} size={12} />}
                >
                  {generateLabel}
                </CreateButton>
              </ActionButtons>
            )}
          </>
        )}

      {showAgentInputs && (
        <div>
          {hasFileInputFields && (
            <>
              {fileInputFields.map((field: any) => (
                <FileUploadField
                  key={field.name}
                  field={field}
                  files={filesByField[field.name] || []}
                  onFileChange={onFileChange}
                  onRemoveFile={(fieldName, fileUid) => {
                    setFilesByField((prev) => ({
                      ...prev,
                      [fieldName]:
                        prev[fieldName]?.filter((f) => f.uid !== fileUid) || [],
                    }));
                  }}
                />
              ))}
            </>
          )}
          <ActionButtons>
            <Button variant="secondary" onClick={handleCancel}>
              {cancelLabel}
            </Button>
            <CreateButton
              onClick={handleCreate}
              disabled={
                isSubmitting ||
                (hasFileInputFields &&
                  Object.values(filesByField).every(
                    (files) => files.length === 0,
                  ))
              }
              leftIcon={<FontAwesomeIcon icon={faPlus} size={12} />}
            >
              {generateLabel}
            </CreateButton>
          </ActionButtons>
        </div>
      )}
    </FormSection>
  );
};
